import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Checkbox, FlowLayout, Icon, SignaturePad, Skeleton } from '@/ds'
import { useStay } from '@/app/stay/StayProvider'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { checkinService } from '@/services/checkin'
import { holderTraveler, useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { useApiLang, useSave } from '../useSave'

/**
 * Paso 7 · Normas y firma (CheckIn.dc.html step=7). La firma se envía en una segunda llamada a E5, como
 * hoy; a diferencia de hoy, si falla no se avanza. Resumen en cinco puntos y envío previo del contrato
 * sin endpoint (D29). Contrato completo: documentos del PMS en hoja con iframe (app actual).
 */
export function Firma() {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const lang = useApiLang()
  const { reload } = useStay()
  const { stay, draft, update } = useCheckinFlow()
  const { prev } = neighbours('7', stay, draft.persona, draft.method, draft.entry)
  const completing = draft.mode === 'complete'
  const [summary] = useAsync(`sum:${stay.guestCode}`, (signal) => checkinService.getContractSummary(stay, signal))
  const [signature, setSignature] = useState<string | null>(null)
  const [consent, setConsent] = useState(false)
  const [tried, setTried] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const { saving, error, run } = useSave()

  const travelerId = draft.travelerId
  const email = draft.guest.email || holderTraveler(stay)?.details.email || ''
  const documents = draft.documents.length > 0 ? draft.documents : stay.documents.filter((d) => !d.travelerId || d.travelerId === travelerId)

  const sign = async () => {
    setTried(true)
    if (!signature || !consent || !travelerId) return
    const ok = await run(() => checkinService.saveSignature(stay, travelerId, signature, lang).then(() => true))
    if (!ok) return
    update({ signaturePending: false })
    // La reserva cambia al firmar: se navega y se recarga para que la lista de pasos y la llave lo reflejen.
    go(stepPath(completing ? '1' : '8'))
    reload()
  }

  // El contrato por email solo se promete si la propiedad lo envía (fase 4, A3).
  const byEmail = checkinService.notices.contractByEmail
  const sendBefore = async () => {
    if (!email) return
    const ok = await run(() => checkinService.sendContractBeforeSigning(stay, email).then(() => true))
    if (ok) setSentTo(email)
  }

  return (
    <FlowLayout
      progress={{ ...progressFor('7', stay, draft.persona, draft.method, draft.entry), backLabel: t(completing ? 'v2.checkin.atras.ocupantes' : backLabelKey(prev)), onBack: () => go(stepPath(completing ? '1' : prev)) }}
      title={t('v2.checkin.firma.titulo')}
      intro={byEmail ? t(summary.kind === 'ready' ? 'v2.checkin.firma.intro' : 'v2.checkin.firma.introSinResumen') : summary.kind === 'ready' ? t('v2.checkin.firma.introSinEmail') : undefined}
      primary={{ label: t('v2.checkin.firma.firmar'), onClick: () => void sign(), loading: saving }}
      secondary={email && byEmail ? { label: t('v2.checkin.firma.enviarAntes'), onClick: () => void sendBefore() } : undefined}
      footerMessage={error ?? (sentTo ? t('v2.checkin.firma.enviado', { email: sentTo }) : null)}
    >
      <Card>
        {summary.kind === 'loading' && <Skeleton height={140} radius={12} />}
        {summary.kind === 'ready' && (
          <ol className="checkin__rules">
            {summary.value.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
        )}
        {documents.map((d) => (
          <a key={d.url} className="checkin__link" href={d.url} target="_blank" rel="noreferrer">
            {documents.length > 1 ? d.name : t('v2.checkin.firma.leerContrato')}
            <Icon name="chevron-right" size={20} />
          </a>
        ))}
      </Card>
      <SignaturePad onChange={setSignature} {...(tried && !signature ? { error: t('v2.legacy.alerts.Please_sign') } : {})} />
      <div>
        <Checkbox checked={consent} onChange={setConsent}>
          {t('v2.checkin.firma.consentimiento')}
        </Checkbox>
        {tried && !consent && (
          <p className="checkin__error" role="alert">
            {t('v2.checkin.firma.marcaCasilla')}
          </p>
        )}
      </div>
    </FlowLayout>
  )
}
